import { Inject } from '@angular/core';
import templateSource from './view.html';
              import { Component } from '@angular/core';
import Wiz from 'src/wiz';
let wiz = new Wiz('/wiz').app('page.kiera.jobs');
import { OnDestroy, OnInit } from '@angular/core';
import { Service } from 'src/libs/portal/season/service';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';

@Component({
 standalone: false,
 selector: 'wiz-page-kiera-jobs',
template: templateSource || '',
    styles: [`

/* file: /opt/app/project/main/.kiera-build/src/app/page.kiera.jobs/view.scss */
:host {
  display: block;
}`],
})
export class PageKieraJobsComponent implements OnInit, OnDestroy {
    public activeTab: string = 'running';
    public loading: boolean = false;
    public error: string = '';
    public rows: any[] = [];
    public summary: any = { statuses: {}, active: 0, total: 0 };
    public broker: any = { db: { queues: [] }, worker: {}, redis: {} };
    public completedJobsEnabled: boolean = true;
    public pageSizes: number[] = [30, 50, 100];
    public pagination: any = { page: 1, dump: 30, total: 0 };
    public filters: any = { origin: '', status: '', date_from: '', date_to: '', text: '' };
    public originOptions: string[] = ['ipmi_scan', 'relay', 'cluster', 'actions', 'agent', 'system', 'unknown'];
    public deletingId: number = 0;
    public bulkDeleting: boolean = false;
    public bulkCancelableTotal: number = 0;
    private langSub?: Subscription;

    constructor(@Inject( Service) public service: Service,@Inject( TranslateService)  private translate: TranslateService) { }

    public async ngOnInit() {
        await this.service.init();
        await this.service.auth.allow('/access');
        await this.service.auth.loadPermissions();
        this.langSub = this.translate.onLangChange.subscribe(async () => {
            await this.load(1);
        });
        await this.load(1);
    }

    public ngOnDestroy() {
        if (this.langSub) this.langSub.unsubscribe();
    }

    public async load(page?: number) {
        this.loading = true;
        this.error = '';
        const nextPage = Math.max(1, Number(page || this.pagination.page || 1));
        this.pagination.page = nextPage;
        await this.service.render();

        const { code, data } = await wiz.call('list', {
            tab: this.activeTab,
            page: this.pagination.page,
            dump: this.pagination.dump,
            origin: this.filters.origin,
            status: this.filters.status,
            date_from: this.filters.date_from,
            date_to: this.filters.date_to,
            text: this.filters.text,
        });

        if (code === 200) {
            this.completedJobsEnabled = data?.log_policy?.completed_jobs !== false;
            if (!this.completedJobsEnabled && this.activeTab === 'completed') {
                this.activeTab = 'running';
                this.loading = false;
                await this.service.render();
                await this.load(1);
                return;
            }
            this.rows = data?.rows || [];
            this.summary = data?.summary || { statuses: {}, active: 0, total: 0 };
            this.broker = data?.broker || { db: { queues: [] }, worker: {}, redis: {} };
            this.pagination.total = Number(data?.total || 0);
            this.pagination.page = Number(data?.page || this.pagination.page || 1);
            this.pagination.dump = Number(data?.dump || this.pagination.dump || 30);
            this.bulkCancelableTotal = Number(data?.bulk_cancelable || 0);
        } else {
            this.rows = [];
            this.pagination.total = 0;
            this.bulkCancelableTotal = 0;
            this.error = data || 'Failed to load jobs.';
        }

        this.loading = false;
        await this.service.render();
    }

    public async switchTab(tab: string) {
        if (tab === 'completed' && !this.completedJobsEnabled) return;
        this.activeTab = tab;
        if (this.filters.status && !this.statusOptions().includes(this.filters.status)) {
            this.filters.status = '';
        }
        await this.load(1);
    }

    public async changePageSize(size: any) {
        const next = Number(size || 30);
        this.pagination.dump = this.pageSizes.includes(next) ? next : 30;
        await this.load(1);
    }

    public statusOptions() {
        return this.activeTab === 'completed'
            ? ['completed', 'failed', 'cancelled', 'review-needed']
            : ['pending', 'running'];
    }

    public async changeFilter(key: string, value: any) {
        this.filters[key] = String(value || '').trim();
        await this.load(1);
    }

    public async applySearch() {
        this.filters.text = String(this.filters.text || '').trim();
        await this.load(1);
    }

    public async onSearchKeydown(event: KeyboardEvent) {
        if (event.key !== 'Enter' || (event as any).isComposing) return;
        event.preventDefault();
        await this.applySearch();
    }

    public async clearFilters() {
        this.filters = { origin: '', status: '', date_from: '', date_to: '', text: '' };
        await this.load(1);
    }

    public hasFilters() {
        return !!(this.filters.origin || this.filters.status || this.filters.date_from || this.filters.date_to || this.filters.text);
    }

    public totalPages() {
        return Math.max(1, Math.ceil(Number(this.pagination.total || 0) / Number(this.pagination.dump || 30)));
    }

    public async movePage(delta: number) {
        const next = Math.min(this.totalPages(), Math.max(1, Number(this.pagination.page || 1) + delta));
        if (next === this.pagination.page) return;
        await this.load(next);
    }

    public pageCounterText() {
        return this.translate.instant('page.jobs.page_counter', {
            page: this.pagination.page || 1,
            pages: this.totalPages(),
            total: this.pagination.total || 0,
        });
    }

    public statusCount(status: string) {
        return Number(this.summary?.statuses?.[status] || 0);
    }

    public closedCount() {
        if (!this.completedJobsEnabled) return 0;
        return this.statusCount('completed') + this.statusCount('failed') + this.statusCount('cancelled') + this.statusCount('review-needed');
    }

    public canCancel(row: any) {
        return !!row?.cancelable && !this.bulkDeleting && this.service.auth.can('job_management', 'remove');
    }

    public canBulkCancel() {
        return this.activeTab === 'running'
            && !this.loading
            && !this.bulkDeleting
            && this.bulkCancelableTotal > 0
            && this.service.auth.can('job_management', 'remove');
    }

    public async cancelJob(row: any) {
        if (!this.canCancel(row)) return;
        const confirmed = await this.service.modal.show({
            title: this.translate.instant('page.jobs.cancel_title'),
            message: this.translate.instant('page.jobs.cancel_message', { id: row.id }),
            action: this.translate.instant('page.jobs.cancel'),
            actionBtn: 'error',
            status: 'error',
        });
        if (!confirmed) return;

        this.deletingId = Number(row.id || 0);
        await this.service.render();
        const { code, data } = await wiz.call('remove', { id: row.id });
        this.deletingId = 0;
        if (code === 200) {
            await this.service.modal.success(this.translate.instant('page.jobs.cancelled'));
            await this.load(this.pagination.page);
            return;
        }
        await this.service.modal.error(data || this.translate.instant('page.jobs.cancel_failed'));
        await this.service.render();
    }

    public async cancelBulkJobs() {
        if (!this.canBulkCancel()) return;
        const confirmed = await this.service.modal.show({
            title: this.translate.instant('page.jobs.bulk_cancel_title'),
            message: this.translate.instant('page.jobs.bulk_cancel_message', { count: this.bulkCancelableTotal }),
            action: this.translate.instant('page.jobs.bulk_cancel', { count: this.bulkCancelableTotal }),
            actionBtn: 'error',
            status: 'error',
        });
        if (!confirmed) return;

        this.bulkDeleting = true;
        await this.service.render();
        const { code, data } = await wiz.call('remove', {
            bulk: 'filtered',
            tab: this.activeTab,
            origin: this.filters.origin,
            status: this.filters.status,
            date_from: this.filters.date_from,
            date_to: this.filters.date_to,
            text: this.filters.text,
        });
        this.bulkDeleting = false;
        if (code === 200) {
            const cancelled = Number(data?.cancelled_count || 0);
            const skipped = Number(data?.skipped_count || 0);
            if (cancelled > 0 && skipped > 0) {
                await this.service.modal.success(this.translate.instant('page.jobs.bulk_cancelled_with_skips', { count: cancelled, skipped }));
            } else if (cancelled > 0) {
                await this.service.modal.success(this.translate.instant('page.jobs.bulk_cancelled', { count: cancelled }));
            } else {
                await this.service.modal.success(this.translate.instant('page.jobs.bulk_cancel_none'));
            }
            await this.load(1);
            return;
        }
        await this.service.modal.error(data || this.translate.instant('page.jobs.bulk_cancel_failed'));
        await this.service.render();
    }

    public statusClass(status: string) {
        switch (String(status || '').toLowerCase()) {
            case 'pending': return 'bg-amber-50 text-amber-700 border-amber-200';
            case 'running': return 'bg-sky-50 text-sky-700 border-sky-200';
            case 'completed': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'failed': return 'bg-rose-50 text-rose-700 border-rose-200';
            case 'cancelled': return 'bg-zinc-100 text-zinc-600 border-zinc-200';
            case 'review-needed': return 'bg-violet-50 text-violet-700 border-violet-200';
            default: return 'bg-zinc-100 text-zinc-600 border-zinc-200';
        }
    }

    public brokerStatusClass(status: string) {
        if (status === 'ok') return 'bg-emerald-50 text-emerald-700 border-emerald-200';
        if (status === 'unknown') return 'bg-amber-50 text-amber-700 border-amber-200';
        return 'bg-rose-50 text-rose-700 border-rose-200';
    }

    public originClass(origin: string) {
        switch (String(origin || '').toLowerCase()) {
            case 'ipmi_scan': return 'bg-cyan-50 text-cyan-700 border-cyan-200';
            case 'relay': return 'bg-indigo-50 text-indigo-700 border-indigo-200';
            case 'cluster': return 'bg-orange-50 text-orange-700 border-orange-200';
            case 'actions': return 'bg-emerald-50 text-emerald-700 border-emerald-200';
            case 'agent': return 'bg-violet-50 text-violet-700 border-violet-200';
            default: return 'bg-zinc-100 text-zinc-600 border-zinc-200';
        }
    }

    public originLabel(row: any) {
        const origin = String(row?.origin || row?.source || 'unknown').trim() || 'unknown';
        const key = `page.jobs.origin.${origin}`;
        const translated = this.translate.instant(key);
        if (translated && translated !== key) return translated;
        return this.shortText(row?.origin_label || origin);
    }

    public originFilterLabel(origin: string) {
        const key = `page.jobs.origin.${origin}`;
        const translated = this.translate.instant(key);
        return translated && translated !== key ? translated : this.shortText(origin);
    }

    public statusLabel(status: string) {
        const key = `page.jobs.status.${status}`;
        const translated = this.translate.instant(key);
        return translated && translated !== key ? translated : this.shortText(status);
    }

    public queueTotal(queue: any) {
        return Number(queue?.messages || 0);
    }

    public shortText(value: any, fallback: string = '-') {
        const text = String(value || '').trim();
        return text || fallback;
    }

    public rowTime(row: any) {
        return row?.updated || row?.created || '-';
    }
}

export default PageKieraJobsComponent;
